import { PILLARS } from "@/data/site";
import FinalCTA from "@/components/FinalCTA";
import { ButtonLink, Eyebrow, FadeIn, PageHero, usePageMeta } from "@/components/blocks";

export default function Programs() {
  usePageMeta(
    "Programs | StrengthLeap — Live 1:1 Coaching Pathways",
    "RECOVER, BUILD, PERFORM, LONGEVITY — four live 1:1 coaching pathways delivered over private video sessions. Desk pain, strength, HYROX, marathon and senior fitness."
  );

  return (
    <main>
      <PageHero
        testid="programs-page"
        eyebrow="The 4 Coaching Pathways"
        lines={["One body.", "Four pathways."]}
        copy="Every pathway is delivered live — private 1:1 video sessions with real-time correction. Enter where you are: pain, plateau, starting line or start line."
      />

      <section className="bg-bone px-6 pb-28 text-ink sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1440px]">
          {PILLARS.map((p, i) => (
            <div
              key={p.key}
              id={p.key === "build" ? "build" : p.key === "longevity" ? "longevity" : undefined}
              data-testid={`program-detail-${p.key}`}
              className="grid items-center gap-10 border-t border-ink/15 py-16 last:border-b sm:py-20 lg:grid-cols-2 lg:gap-20"
            >
              <div className={`overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="aspect-[16/11] overflow-hidden bg-ink/5">
                  <img
                    src={p.image}
                    alt={`${p.title} coaching pathway`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <FadeIn>
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink/50">
                    Pathway {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight sm:text-6xl">
                    {p.title}
                  </h2>
                  <p className="mt-3 font-display text-xl font-bold uppercase tracking-tight text-ink/60 sm:text-2xl">
                    {p.tagline}
                  </p>
                  <div className="mt-8 space-y-6 border-t border-ink/15 pt-8">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">For</p>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-smoke sm:text-base">{p.forWho}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">Delivery</p>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-smoke sm:text-base">{p.delivery}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/45">Outcome</p>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/85 sm:text-base">{p.outcome}</p>
                    </div>
                  </div>
                  <div className="mt-10">
                    <ButtonLink to="/start" variant="ink" testid={`program-cta-${p.key}`}>
                      Start With {p.title}
                    </ButtonLink>
                  </div>
                </FadeIn>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink px-6 py-24 text-bone sm:px-10 lg:px-16">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div>
            <FadeIn>
              <Eyebrow>Not Sure Where You Fit?</Eyebrow>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight sm:text-5xl">
                The live diagnostic decides <span className="text-lime">— not guesswork.</span>
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <ButtonLink to="/start" variant="lime" testid="programs-cta-assessment">
              Schedule Your Assessment
            </ButtonLink>
          </FadeIn>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
